import { AfterViewInit, Component, Input, OnInit, ViewChildren } from "@angular/core";
import { QuestionNPS } from "../../../../model/forms/question-NPS.model";
import { QuestionRangeValue } from "../../../../model/forms/question-range.model";
import { FormCompletionBaseComponent } from "../form-completion-base.component";

@Component({
  selector: "app-question-nps",
  templateUrl: "./question-NPS.component.html",
  styleUrl: "./question-NPS.component.scss",
  standalone: false,
})
export class QuestionNPSComponent
  extends FormCompletionBaseComponent
  implements OnInit, AfterViewInit
{
  @ViewChildren("input") inputRefs;

  @Input() public question!: QuestionNPS;

  public currentValue;
  public scaleValue = 0;

  ngOnInit() {
    this.scaleValue = this.hasValue ? Number(this.control.value) : this.sliderMin;
  }

  ngAfterViewInit() {
    if (!this.inputRefs?._results) {
      return;
    }
    for (let i = 0; i < this.inputRefs._results.length; i++) {
      this.inputRefs._results[i].nativeElement.name = this.question.getName();
    }
  }

  public selectOption(value: number): void {
    if (this.service) {
      this.recordExplicitAnswer(this.question, value);
    } else {
      this.currentValue = value;
    }
  }

  /**
   * Updates the displayed thumb and records a deliberate slider value change.
   * @param value Slider value selected by the respondent.
   */
  public onScaleValueChange(value: number): void {
    this.scaleValue = value;
    this.selectOption(value);
  }

  /**
   * Confirms a pointer interaction even when the thumb remains on its initial visual position.
   * @param value Value reported by the Material slider.
   */
  public confirmScaleValue(value: number): void {
    this.onScaleValueChange(value);
  }

  /**
   * Records supported keyboard slider interactions without treating Tab focus as an answer.
   * @param event Keyboard event emitted by the slider thumb.
   */
  public confirmScaleFromKeyboard(event: KeyboardEvent): void {
    if (this.isSliderInteractionKey(event.key)) {
      this.confirmScaleValue(this.scaleValue);
    }
  }

  public get control() {
    return this.service.formGroup.controls[this.question.getName()];
  }

  public get useScale(): boolean {
    return !!this.question.viewMeta?.useScale;
  }

  public get useGradient(): boolean {
    return !!this.question.viewMeta?.useGradient;
  }
  public get gradientColour1(): string {
    return this.question.viewMeta?.gradientColour1 || "";
  }
  public get gradientColour2(): string {
    return this.question.viewMeta?.gradientColour2 || "";
  }
  public get scaleGradient(): string {
    const c1 =
      this.service.themeService.getThemeColor(
        this.service.themeService.ThemeItemEnum.gradientStart
      ) ||
      this.gradientColour1 ||
      "#e0e0e0";
    const c2 =
      this.service.themeService.getThemeColor(
        this.service.themeService.ThemeItemEnum.gradientEnd
      ) ||
      this.gradientColour2 ||
      "#6c6c6c";
    return `linear-gradient(to right, ${c1}, ${c2})`;
  }

  public get scaleRanges(): QuestionRangeValue[] {
    return this.question.questionRangeGroup.ranges.filter((r) => r.value !== -999);
  }

  public get firstLabel(): string {
    return this.scaleRanges[0]?.description ?? "";
  }

  public get lastLabel(): string {
    const ranges = this.scaleRanges;
    return ranges[ranges.length - 1]?.description ?? "";
  }

  private get scaleValues(): number[] {
    return this.scaleRanges.map((r) => r.value);
  }

  public get sliderMin(): number {
    return Math.min(...this.scaleValues);
  }

  public get sliderMax(): number {
    return Math.max(...this.scaleValues);
  }

  public labelPosition(value: number): number {
    const span = this.sliderMax - this.sliderMin;
    if (span <= 0) {
      return 0;
    }
    return ((value - this.sliderMin) / span) * 100;
  }

  public get hasValue(): boolean {
    const value = this.control?.value;
    return value !== null && value !== undefined && value !== "";
  }

  public descriptionFor = (value: number): string => {
    if (value === null || value === undefined || (value as unknown) === "") {
      return "";
    }
    return (
      this.question.questionRangeGroup.ranges.find((r) => r.value === value)?.description ??
      `${value}`
    );
  };
}
